// Fiche d'une visite de maintenance : fenêtre de passage, affectations (planification), relevés des points de contrôle
// (échantillon tournant + points critiques), écarts, points laissés forcés, puis « Terminer » qui crée le rapport : le bon
// d'intervention existant, SANS prix, à faire signer. Aucun prix n'est servi ni affiché sur cet écran.
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, CheckCircle2, FileSignature, Play, Plus, Save, Trash2, UserPlus } from 'lucide-react';
import { LIBELLES_MODE_VISITE, LIBELLES_STATUT_ECART } from '@shared/contrats/contrats.ts';
import { api, ErreurApi, messageErreur } from '../../lib/api.ts';
import { date, pluriel } from '../../lib/format.ts';
import { useSession } from '../../lib/session.tsx';
import { Alerte, Badge, Bouton, Chargement, Champ, ChampChoix, ChampTexte, Dialogue, EntetePage, EtatErreur, EtatVide, LienBouton, Panneau,
  Progression, useConfirmation, useNotifier } from '../../components/ui.tsx';
import { BadgeStatutVisite, CRENEAUX_LIBELLES, GardeVisites } from './commun.tsx';
import type { Creneau, DetailVisite, LigneFiche, Personne } from './types.ts';

export function PageVisite() {
  return <GardeVisites><FicheVisite /></GardeVisites>;
}

function FicheVisite() {
  const { id = '' } = useParams();
  const qc = useQueryClient(); const notifier = useNotifier(); const { peut } = useSession();
  const q = useQuery({ queryKey: ['visite', id], queryFn: () => api.get<DetailVisite>(`/api/visites/${id}`) });
  const maj = (d: DetailVisite) => { qc.setQueryData(['visite', id], d); qc.invalidateQueries({ queryKey: ['visites'] }); };
  const demarrer = useMutation({
    mutationFn: () => api.post<DetailVisite>(`/api/visites/${id}/demarrer`, {}),
    onSuccess: (d) => { maj(d); notifier('Visite démarrée.'); }, onError: (e) => notifier(messageErreur(e), true),
  });
  const [manquants, setManquants] = useState<{ cle: string; libelle: string; equipement: string | null }[]>([]);
  const terminer = useMutation({
    mutationFn: () => api.post<DetailVisite>(`/api/visites/${id}/terminer`, {}),
    onSuccess: (d) => { setManquants([]); maj(d); notifier('Rapport créé : faites-le signer sur le bon d’intervention.'); },
    onError: (e) => {
      const det = e instanceof ErreurApi ? (e.details as { manquants?: typeof manquants } | undefined) : undefined;
      setManquants(det?.manquants ?? []);
      notifier(messageErreur(e), true);
    },
  });

  if (q.isLoading) return <Chargement />;
  if (q.isError || !q.data) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  const d = q.data; const v = d.visite;
  const ouverte = v.statut !== 'realisee' && v.statut !== 'annulee' && !v.bon_intervention_id;
  const encadrement = peut('contrats.ecrire') || peut('visites.planifier');
  const pct = d.fiche.total ? Math.round((d.fiche.faites / d.fiche.total) * 100) : 0;
  const peutDemarrer = d.droits.realiser && ouverte && (v.statut === 'a_planifier' || v.statut === 'planifiee');
  const peutTerminer = d.droits.realiser && ouverte && v.statut === 'en_cours';

  return (
    <div className="pile-l">
      <EntetePage titre={`${LIBELLES_MODE_VISITE[v.mode]} n° ${v.rang}`}
        fil={<Link to="/interventions/visites">Visites de maintenance</Link>}
        sousTitre={<span className="cm-entete-infos">
          <span>{v.site}</span>
          <span className="mono">{v.reference}</span>
          {v.contrat_reference && (encadrement
            ? <Link to={`/contrats/${v.contrat_id}`}>contrat <span className="mono">{v.contrat_reference}</span></Link>
            : <span>contrat <span className="mono">{v.contrat_reference}</span></span>)}
        </span>}
        actions={<>
          {peutDemarrer && <Bouton variante="principal" icone={<Play aria-hidden />} chargement={demarrer.isPending} onClick={() => demarrer.mutate()}>Démarrer la visite</Bouton>}
          {d.bon && <LienBouton variante="principal" icone={<FileSignature aria-hidden />} to={`/interventions/bons/${d.bon.id}`}>Rapport {d.bon.reference}</LienBouton>}
        </>} />
      <div className="int-ligne-badges">
        <BadgeStatutVisite statut={v.statut} enRetard={v.en_retard} />
        {v.demo && <Badge ton="neutre">Démo</Badge>}
      </div>
      {v.en_retard && (
        <Alerte ton="danger" titre="Visite en retard">La fenêtre de passage s’est terminée le {date(v.fenetre_fin)} sans rapport signé.</Alerte>
      )}
      {d.bon && v.statut !== 'realisee' && (
        <Alerte ton="info" titre={`Rapport ${d.bon.reference} créé`}>
          Les relevés sont figés. La visite sera réalisée dès que le bon d’intervention sera signé par le client.
        </Alerte>
      )}

      <PanneauPlanification d={d} id={id} ouverte={ouverte} maj={maj} />

      <Panneau titre="Points de contrôle"
        actions={<span className="petit discret">{d.fiche.faites} / {pluriel(d.fiche.total, 'point', 'points')}</span>}>
        <div className="pile">
          <Progression pct={pct} libelle={d.fiche.gamme
            ? `Gamme « ${d.fiche.gamme.nom} » (version ${d.fiche.gamme.version}) · échantillon tournant ${d.fiche.taux_echantillon} % · points critiques à chaque visite`
            : 'Aucune gamme de contrôle pour ce contrat.'} />
          {d.fiche.lignes.length === 0
            ? <EtatVide icone={<CalendarClock />} titre="Aucun point de contrôle" texte="Le périmètre du contrat ne contient aucun équipement pour ce site." />
            : <Releves d={d} id={id} saisie={d.droits.realiser && ouverte && v.statut === 'en_cours'} maj={maj} />}
          {d.droits.realiser && ouverte && v.statut !== 'en_cours' && d.fiche.lignes.length > 0 && (
            <p className="petit discret" style={{ margin: 0 }}>Démarrez la visite pour saisir les relevés.</p>
          )}
        </div>
      </Panneau>

      {d.ecarts.length > 0 && (
        <Panneau titre={pluriel(d.ecarts.length, 'écart', 'écarts')}>
          <ul className="liste cm-ecarts">
            {d.ecarts.map((e) => {
              const l = d.fiche.lignes.find((x) => x.cle === e.ligne_cle);
              return (
                <li key={e.id} className="liste-element">
                  <span className="liste-element-corps">
                    <span className="liste-element-titre">{l?.libelle ?? e.ligne_cle}</span>
                    <span className="petit discret">{l?.equipement ?? 'Site'}{e.recommandation ? ` — ${e.recommandation}` : ''}</span>
                    <span className="int-ligne-badges">
                      <Badge ton={e.gravite === 'critique' ? 'danger' : e.gravite === 'majeure' ? 'ambre' : 'neutre'}>Écart {e.gravite}</Badge>
                      <Badge ton="neutre" icone={false}>{LIBELLES_STATUT_ECART[e.statut as keyof typeof LIBELLES_STATUT_ECART] ?? e.statut}</Badge>
                    </span>
                  </span>
                </li>
              );
            })}
          </ul>
        </Panneau>
      )}

      <PanneauForcages d={d} id={id} saisie={d.droits.realiser && ouverte} maj={maj} />

      {peutTerminer && (
        <Panneau titre="Fin de visite">
          <div className="pile">
            <p className="petit" style={{ margin: 0 }}>Terminer crée le rapport de visite : un bon d’intervention sans prix, avec les relevés, les écarts et les points laissés forcés, à faire signer par le client.</p>
            {manquants.length > 0 && (
              <Alerte ton="ambre" titre={`${pluriel(manquants.length, 'point', 'points')} sans résultat`}>
                <ul className="cm-manque">{manquants.map((m) => <li key={m.cle}>{m.libelle}{m.equipement ? ` — ${m.equipement}` : ''}</li>)}</ul>
              </Alerte>
            )}
            <div className="barre-actions">
              <Bouton variante="principal" icone={<CheckCircle2 aria-hidden />} chargement={terminer.isPending} disabled={d.fiche.faites < d.fiche.total}
                onClick={() => terminer.mutate()}>Terminer et créer le rapport</Bouton>
            </div>
            {d.fiche.faites < d.fiche.total && <p className="tres-petit discret" style={{ margin: 0 }}>Tous les points doivent avoir un résultat (OK, KO ou sans objet).</p>}
          </div>
        </Panneau>
      )}
    </div>
  );
}

// ─── Fenêtre et affectations ───
function PanneauPlanification({ d, id, ouverte, maj }: { d: DetailVisite; id: string; ouverte: boolean; maj: (d: DetailVisite) => void }) {
  const v = d.visite; const notifier = useNotifier(); const { element, confirmer } = useConfirmation();
  const [fenetre, setFenetre] = useState(false);
  const [affecter, setAffecter] = useState(false);
  const retirer = useMutation({
    mutationFn: (aid: string) => api.del<DetailVisite>(`/api/visites/${id}/affectations/${aid}`),
    onSuccess: (r) => { maj(r); notifier('Affectation retirée.'); }, onError: (e) => notifier(messageErreur(e), true),
  });
  const planifier = d.droits.planifier && ouverte;
  return (
    <Panneau titre="Planification"
      actions={planifier ? <Bouton compact icone={<UserPlus aria-hidden />} onClick={() => setAffecter(true)}>Affecter</Bouton> : undefined}>
      <div className="pile">
        <div className="cm-fenetre">
          <div>
            <span className="petit discret">Date prévue</span>
            <strong>{date(v.date_due)}</strong>
          </div>
          <div>
            <span className="petit discret">Fenêtre de passage</span>
            <strong>du {date(v.fenetre_debut)} au {date(v.fenetre_fin)}</strong>
          </div>
          {planifier && <Bouton compact variante="fantome" onClick={() => setFenetre(true)}>Ajuster la fenêtre</Bouton>}
        </div>
        {v.calcul_explication && <p className="tres-petit discret" style={{ margin: 0 }}>{v.calcul_explication}</p>}
        {d.affectations.length === 0
          ? <p className="petit discret" style={{ margin: 0 }}>Personne n’est encore affecté à cette visite.</p>
          : (
            <ul className="liste">
              {d.affectations.map((a) => (
                <li key={a.id} className="liste-element cm-affectation">
                  <span className="liste-element-corps">
                    <span className="liste-element-titre">{a.nom}</span>
                    <span className="petit discret">{date(a.jour)} · {CRENEAUX_LIBELLES[a.creneau]}{a.note ? ` · ${a.note}` : ''}</span>
                  </span>
                  {planifier && (
                    <Bouton compact variante="fantome" icone={<Trash2 aria-hidden />} aria-label={`Retirer ${a.nom}`} chargement={retirer.isPending && retirer.variables === a.id}
                      onClick={async () => { if (await confirmer('Retirer l’affectation', `${a.nom} ne sera plus affecté à cette visite le ${date(a.jour)}.`, 'Retirer')) retirer.mutate(a.id); }} />
                  )}
                </li>
              ))}
            </ul>
          )}
      </div>
      {planifier && <DialogueFenetre d={d} id={id} ouvert={fenetre} fermer={() => setFenetre(false)} maj={maj} />}
      {planifier && <DialogueAffectation d={d} id={id} ouvert={affecter} fermer={() => setAffecter(false)} maj={maj} />}
      {element}
    </Panneau>
  );
}

function DialogueFenetre({ d, id, ouvert, fermer, maj }: { d: DetailVisite; id: string; ouvert: boolean; fermer: () => void; maj: (d: DetailVisite) => void }) {
  const notifier = useNotifier();
  const [debut, setDebut] = useState(d.visite.fenetre_debut);
  const [fin, setFin] = useState(d.visite.fenetre_fin);
  const [motif, setMotif] = useState('');
  useEffect(() => { if (ouvert) { setDebut(d.visite.fenetre_debut); setFin(d.visite.fenetre_fin); setMotif(''); } }, [ouvert, d.visite.fenetre_debut, d.visite.fenetre_fin]);
  const contient = debut <= d.visite.date_due && d.visite.date_due <= fin;
  const m = useMutation({
    mutationFn: () => api.patch<DetailVisite>(`/api/visites/${id}/fenetre`, { fenetreDebut: debut, fenetreFin: fin, motif: motif.trim() }),
    onSuccess: (r) => { maj(r); fermer(); notifier('Fenêtre de passage ajustée.'); }, onError: (e) => notifier(messageErreur(e), true),
  });
  return (
    <Dialogue titre="Ajuster la fenêtre de passage" ouvert={ouvert} fermer={fermer}
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" disabled={!contient || motif.trim().length < 3 || !debut || !fin} chargement={m.isPending} onClick={() => m.mutate()}>Enregistrer</Bouton></>}>
      <div className="pile">
        <div className="grille-2">
          <Champ libelle="Du" type="date" value={debut} onChange={(e) => setDebut(e.target.value)} />
          <Champ libelle="Au" type="date" value={fin} onChange={(e) => setFin(e.target.value)} />
        </div>
        {!contient && <p className="petit texte-danger" style={{ margin: 0 }}>La fenêtre doit contenir la date prévue ({date(d.visite.date_due)}).</p>}
        <ChampTexte libelle="Motif" value={motif} onChange={(e) => setMotif(e.target.value)} rows={2} maxLength={500} aide="Gardé au journal de la visite." />
      </div>
    </Dialogue>
  );
}

function DialogueAffectation({ d, id, ouvert, fermer, maj }: { d: DetailVisite; id: string; ouvert: boolean; fermer: () => void; maj: (d: DetailVisite) => void }) {
  const notifier = useNotifier();
  const personnes = useQuery({ queryKey: ['visite-personnes', id], queryFn: () => api.get<{ personnes: Personne[] }>(`/api/visites/${id}/personnes`), enabled: ouvert });
  const [userId, setUserId] = useState('');
  const [jour, setJour] = useState(d.visite.fenetre_debut);
  const [creneau, setCreneau] = useState<Creneau>('journee');
  const [note, setNote] = useState('');
  const [avertissements, setAvertissements] = useState<string[]>([]);
  useEffect(() => { if (ouvert) { setUserId(''); setNote(''); setAvertissements([]); setCreneau('journee'); setJour(d.visite.date_due); } }, [ouvert, d.visite.date_due]);
  const horsFenetre = !!jour && (jour < d.visite.fenetre_debut || jour > d.visite.fenetre_fin);
  const m = useMutation({
    mutationFn: () => api.post<DetailVisite>(`/api/visites/${id}/affectations`, { userId, jour, creneau, ...(note.trim() ? { note: note.trim() } : {}) }),
    onSuccess: (r) => {
      maj(r);
      if (r.avertissements?.length) { setAvertissements(r.avertissements); notifier('Affectation enregistrée, avec un avertissement.'); }
      else { fermer(); notifier('Affectation enregistrée.'); }
    },
    onError: (e) => notifier(messageErreur(e), true),
  });
  const options = (personnes.data?.personnes ?? []).map((p) => ({ valeur: p.id, libelle: p.acces ? p.nom : `${p.nom} (pas d’accès à ce site)`, acces: p.acces }));
  return (
    <Dialogue titre="Affecter une personne" ouvert={ouvert} fermer={fermer}
      pied={avertissements.length
        ? <Bouton variante="principal" onClick={fermer}>Fermer</Bouton>
        : <><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" disabled={!userId || !jour} chargement={m.isPending} onClick={() => m.mutate()}>Affecter</Bouton></>}>
      <div className="pile">
        {avertissements.length > 0 && (
          <Alerte ton="ambre" titre="Affectation enregistrée — à vérifier">
            <ul className="cm-manque">{avertissements.map((a) => <li key={a}>{a}</li>)}</ul>
          </Alerte>
        )}
        {personnes.isLoading && <Chargement lignes={2} />}
        {personnes.isError && <EtatErreur erreur={personnes.error} reessayer={() => personnes.refetch()} />}
        {personnes.data && !avertissements.length && (
          <>
            <div className="champ">
              <label className="champ-libelle" htmlFor="cm-personne">Personne</label>
              <select id="cm-personne" className="saisie" value={userId} onChange={(e) => setUserId(e.target.value)}>
                <option value="">Choisir une personne</option>
                {options.map((o) => <option key={o.valeur} value={o.valeur} disabled={!o.acces}>{o.libelle}</option>)}
              </select>
              <span className="champ-aide">Seules les personnes ayant accès au site peuvent être affectées.</span>
            </div>
            <div className="grille-2">
              <Champ libelle="Jour" type="date" value={jour} onChange={(e) => setJour(e.target.value)}
                aide={horsFenetre ? 'Hors de la fenêtre de passage : la visite sera en retard si elle a lieu après.' : undefined} />
              <ChampChoix libelle="Créneau" value={creneau} onChange={(e) => setCreneau(e.target.value as Creneau)}
                options={(Object.keys(CRENEAUX_LIBELLES) as Creneau[]).map((c) => ({ valeur: c, libelle: CRENEAUX_LIBELLES[c] }))} />
            </div>
            <Champ libelle="Note (facultatif)" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
            <p className="tres-petit discret" style={{ margin: 0 }}>Un conflit avec un chantier ou une autre visite le même jour est signalé, jamais bloqué.</p>
          </>
        )}
      </div>
    </Dialogue>
  );
}

// ─── Relevés ───
interface Saisie { resultat: 'ok' | 'ko' | 'sans_objet' | null; valeur: string; texte: string; commentaire: string }
const saisieDe = (l: LigneFiche): Saisie => ({
  resultat: l.releve?.resultat ?? null,
  valeur: l.releve?.valeur_num != null ? String(l.releve.valeur_num).replace('.', ',') : '',
  texte: (l.type_releve === 'version' ? l.releve?.version_lue : l.releve?.valeur_texte) ?? '',
  commentaire: l.releve?.commentaire ?? '',
});
const nombreSaisi = (s: string) => { const t = s.trim().replace(/\s/g, '').replace(',', '.'); return /^-?\d+(\.\d+)?$/.test(t) ? Number(t) : null; };
/** Même règle que le serveur : une valeur hors seuils est TOUJOURS un KO. */
function resultatPrevu(l: LigneFiche, s: Saisie) {
  const n = l.type_releve === 'valeur' ? nombreSaisi(s.valeur) : null;
  if (n != null && ((l.seuil_min != null && n < l.seuil_min) || (l.seuil_max != null && n > l.seuil_max))) return 'ko';
  if (s.resultat) return s.resultat;
  if (l.type_releve === 'valeur' && n != null) return 'ok';
  if ((l.type_releve === 'version' || l.type_releve === 'texte') && s.texte.trim()) return 'ok';
  return null;
}

function Releves({ d, id, saisie, maj }: { d: DetailVisite; id: string; saisie: boolean; maj: (d: DetailVisite) => void }) {
  const notifier = useNotifier();
  const initial = useMemo(() => Object.fromEntries(d.fiche.lignes.map((l) => [l.cle, saisieDe(l)])), [d.fiche.lignes]);
  const [brouillon, setBrouillon] = useState<Record<string, Saisie>>(initial);
  const [modifiees, setModifiees] = useState<Set<string>>(new Set());
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  useEffect(() => { setBrouillon(initial); setModifiees(new Set()); setErreurs({}); }, [initial]);
  const changer = (cle: string, p: Partial<Saisie>) => {
    setBrouillon((b) => ({ ...b, [cle]: { ...b[cle], ...p } }));
    setModifiees((m) => new Set(m).add(cle));
    setErreurs((e) => { const { [cle]: _x, ...reste } = e; return reste; });
  };
  const envoyer = useMutation({
    mutationFn: (lignes: Record<string, unknown>[]) => api.put<DetailVisite & { envoi: { enregistres: number } }>(`/api/visites/${id}/releves`, { lignes }),
    onSuccess: (r) => { maj(r); notifier(`${pluriel(r.envoi.enregistres, 'relevé enregistré', 'relevés enregistrés')}.`); },
    onError: (e) => {
      const det = e instanceof ErreurApi ? (e.details as { cle?: string } | undefined) : undefined;
      if (det?.cle) setErreurs((x) => ({ ...x, [det.cle!]: messageErreur(e) }));
      notifier(messageErreur(e), true);
    },
  });
  const enregistrer = () => {
    const errs: Record<string, string> = {};
    const lignes = d.fiche.lignes.filter((l) => modifiees.has(l.cle)).map((l) => {
      const s = brouillon[l.cle];
      const res = resultatPrevu(l, s);
      if (l.type_releve === 'valeur' && s.valeur.trim() && nombreSaisi(s.valeur) == null) errs[l.cle] = 'Valeur numérique attendue.';
      else if (res === 'ko' && s.commentaire.trim().length < 3) errs[l.cle] = 'Point KO : un commentaire de 3 caractères au moins est obligatoire.';
      return {
        cle: l.cle, resultat: s.resultat,
        valeurNum: l.type_releve === 'valeur' ? nombreSaisi(s.valeur) : null,
        valeurTexte: l.type_releve === 'texte' ? s.texte.trim() || null : null,
        versionLue: l.type_releve === 'version' ? s.texte.trim() || null : null,
        commentaire: s.commentaire.trim() || null,
      };
    });
    setErreurs(errs);
    if (Object.keys(errs).length) { notifier('Certains points sont à compléter avant l’enregistrement.', true); return; }
    if (lignes.length) envoyer.mutate(lignes);
  };
  const nb = modifiees.size;

  return (
    <div className="pile">
      {saisie && (
        <div className="barre-actions cm-barre-releves">
          <Bouton variante="principal" icone={<Save aria-hidden />} disabled={!nb} chargement={envoyer.isPending} onClick={enregistrer}>
            {nb ? `Enregistrer (${nb})` : 'Enregistrer'}
          </Bouton>
        </div>
      )}
      <ul className="cm-releves">
        {d.fiche.lignes.map((l) => {
          const s = brouillon[l.cle] ?? saisieDe(l);
          const prevu = resultatPrevu(l, s);
          const seuils = l.seuil_min != null || l.seuil_max != null
            ? `Attendu ${l.seuil_min != null ? `≥ ${l.seuil_min}` : ''}${l.seuil_min != null && l.seuil_max != null ? ' et ' : ''}${l.seuil_max != null ? `≤ ${l.seuil_max}` : ''}${l.unite ? ` ${l.unite}` : ''}`
            : null;
          return (
            <li key={l.cle} className={`cm-releve ${prevu === 'ko' ? 'est-ko' : ''}`}>
              <div className="cm-releve-tete">
                <span className="cm-releve-titre">{l.libelle}</span>
                <span className="int-ligne-badges">
                  {l.critique && <Badge ton="danger" icone={false}>Critique</Badge>}
                  {prevu && <Badge ton={prevu === 'ok' ? 'succes' : prevu === 'ko' ? 'danger' : 'neutre'}>{d.libelles.LIBELLES_RESULTAT_RELEVE[prevu] ?? prevu}</Badge>}
                </span>
              </div>
              <span className="petit discret">{l.equipement ?? 'Site'} · {l.famille_libelle}{seuils ? ` · ${seuils}` : ''}</span>
              {l.consigne && <span className="tres-petit discret">{l.consigne}</span>}
              {saisie ? (
                <div className="cm-releve-saisie">
                  {l.type_releve === 'valeur' && (
                    <Champ libelle={`Valeur${l.unite ? ` (${l.unite})` : ''}`} inputMode="decimal" value={s.valeur} onChange={(e) => changer(l.cle, { valeur: e.target.value })} />
                  )}
                  {(l.type_releve === 'texte' || l.type_releve === 'version') && (
                    <Champ libelle={l.type_releve === 'version' ? 'Version lue' : 'Relevé'} value={s.texte} maxLength={l.type_releve === 'version' ? 120 : 2000}
                      onChange={(e) => changer(l.cle, { texte: e.target.value })} />
                  )}
                  <div className="champ">
                    <span className="champ-libelle" id={`res-${l.cle}`}>Résultat</span>
                    <div className="segment" role="group" aria-labelledby={`res-${l.cle}`}>
                      {(['ok', 'ko', 'sans_objet'] as const).map((r) => (
                        <button key={r} type="button" aria-pressed={s.resultat === r} onClick={() => changer(l.cle, { resultat: s.resultat === r ? null : r })}>
                          {d.libelles.LIBELLES_RESULTAT_RELEVE[r] ?? r}
                        </button>
                      ))}
                    </div>
                  </div>
                  {(prevu === 'ko' || s.commentaire) && (
                    <Champ libelle={prevu === 'ko' ? 'Commentaire (obligatoire)' : 'Commentaire'} value={s.commentaire} maxLength={2000}
                      onChange={(e) => changer(l.cle, { commentaire: e.target.value })} erreur={erreurs[l.cle]} />
                  )}
                  {erreurs[l.cle] && prevu !== 'ko' && !s.commentaire && <p className="petit texte-danger" style={{ margin: 0 }}>{erreurs[l.cle]}</p>}
                </div>
              ) : l.releve && (
                <span className="petit">
                  {l.releve.valeur_num != null && <>{String(l.releve.valeur_num).replace('.', ',')}{l.releve.unite ? ` ${l.releve.unite}` : ''} </>}
                  {l.releve.version_lue ?? l.releve.valeur_texte ?? ''}
                  {l.releve.commentaire && <span className="discret"> — {l.releve.commentaire}</span>}
                </span>
              )}
            </li>
          );
        })}
      </ul>
      {saisie && nb > 0 && (
        <div className="barre-actions">
          <Bouton variante="principal" icone={<Save aria-hidden />} chargement={envoyer.isPending} onClick={enregistrer}>Enregistrer ({nb})</Bouton>
        </div>
      )}
    </div>
  );
}

// ─── Points laissés forcés ───
function PanneauForcages({ d, id, saisie, maj }: { d: DetailVisite; id: string; saisie: boolean; maj: (d: DetailVisite) => void }) {
  const notifier = useNotifier(); const { element, confirmer } = useConfirmation();
  const [ajout, setAjout] = useState(false);
  const retirer = useMutation({
    mutationFn: (fid: string) => api.del<DetailVisite>(`/api/visites/${id}/forcages/${fid}`),
    onSuccess: (r) => { maj(r); notifier('Point forcé retiré.'); }, onError: (e) => notifier(messageErreur(e), true),
  });
  if (!saisie && d.forcages.length === 0) return null;
  return (
    <Panneau titre="Points laissés forcés"
      actions={saisie ? <Bouton compact icone={<Plus aria-hidden />} onClick={() => setAjout(true)}>Ajouter</Bouton> : undefined}>
      {d.forcages.length === 0
        ? <p className="petit discret" style={{ margin: 0 }}>Aucun point laissé forcé. Tout point forcé en fin de visite doit être déclaré et justifié : il figure en réserve sur le rapport.</p>
        : (
          <ul className="liste">
            {d.forcages.map((f) => (
              <li key={f.id} className="liste-element cm-affectation">
                <span className="liste-element-corps">
                  <span className="liste-element-titre">{f.point}{f.valeur_forcee ? ` = ${f.valeur_forcee}` : ''}</span>
                  <span className="petit discret">{f.equipement ?? 'Site'} — {f.justification}</span>
                </span>
                {saisie && (
                  <Bouton compact variante="fantome" icone={<Trash2 aria-hidden />} aria-label={`Retirer le point forcé ${f.point}`}
                    onClick={async () => { if (await confirmer('Retirer le point forcé', `« ${f.point} » n’est plus forcé ?`, 'Retirer')) retirer.mutate(f.id); }} />
                )}
              </li>
            ))}
          </ul>
        )}
      {saisie && <DialogueForcage d={d} id={id} ouvert={ajout} fermer={() => setAjout(false)} maj={maj} />}
      {element}
    </Panneau>
  );
}

function DialogueForcage({ d, id, ouvert, fermer, maj }: { d: DetailVisite; id: string; ouvert: boolean; fermer: () => void; maj: (d: DetailVisite) => void }) {
  const notifier = useNotifier();
  const [equipmentId, setEquipmentId] = useState('');
  const [point, setPoint] = useState('');
  const [valeur, setValeur] = useState('');
  const [justification, setJustification] = useState('');
  useEffect(() => { if (ouvert) { setEquipmentId(''); setPoint(''); setValeur(''); setJustification(''); } }, [ouvert]);
  const equipements = useMemo(() => [...new Map(d.fiche.lignes.filter((l) => l.equipment_id).map((l) => [l.equipment_id!, l.equipement ?? ''])).entries()], [d.fiche.lignes]);
  const m = useMutation({
    mutationFn: () => api.post<DetailVisite>(`/api/visites/${id}/forcages`, {
      ...(equipmentId ? { equipmentId } : {}), point: point.trim(), ...(valeur.trim() ? { valeurForcee: valeur.trim() } : {}), justification: justification.trim(),
    }),
    onSuccess: (r) => { maj(r); fermer(); notifier('Point forcé déclaré.'); }, onError: (e) => notifier(messageErreur(e), true),
  });
  return (
    <Dialogue titre="Déclarer un point laissé forcé" ouvert={ouvert} fermer={fermer}
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" disabled={!point.trim() || justification.trim().length < 3} chargement={m.isPending} onClick={() => m.mutate()}>Déclarer</Bouton></>}>
      <div className="pile">
        <ChampChoix libelle="Équipement" value={equipmentId} onChange={(e) => setEquipmentId(e.target.value)} vide="Site (aucun équipement précis)"
          options={equipements.map(([eid, nom]) => ({ valeur: eid, libelle: nom }))} />
        <div className="grille-2">
          <Champ libelle="Point forcé" value={point} onChange={(e) => setPoint(e.target.value)} maxLength={200} placeholder="Consigne de vanne V3" />
          <Champ libelle="Valeur forcée (facultatif)" value={valeur} onChange={(e) => setValeur(e.target.value)} maxLength={120} placeholder="100 %" />
        </div>
        <ChampTexte libelle="Justification" value={justification} onChange={(e) => setJustification(e.target.value)} rows={3} maxLength={1000}
          aide="Obligatoire : elle figure en réserve sur le rapport de visite." />
      </div>
    </Dialogue>
  );
}
